import { type PropsOf, type SlotAlignment } from '@blaze-runtime/engine-domain';

import { type CanvasElementDefinition } from '../../canvas-elements/element-definition';
import { registerElementDefinition } from '../../services/element-definitions.service';

import {
  getContainerDefaults,
  getContainerSlotDefaults,
  getIoConfig,
  getNavigationContainerPropsConfig,
  getNavigationContainerSlotPropsConfig,
} from './_base-container.element';
import {
  type BaseContainerDef,
  type BaseContainerPropsModel,
  CONTAINER_DEFAULT_HEIGHT,
  CONTAINER_INITIAL_SLOT_ID,
} from './_container.models';
import { ContainerComponent } from './container.component';

export type NavHeaderContainerDef = BaseContainerDef<NavHeaderContainerPropsModel>;

// Extend the CanvasElementDefinitions type
declare module '@blaze-runtime/engine-domain' {
  interface CanvasElementDefinitions {
    navHeaderContainer: NavHeaderContainerDef;
  }
}

export function getNavHeaderContainerDefinition(): CanvasElementDefinition<
  'navHeaderContainer',
  'container'
> {
  return {
    type: 'navHeaderContainer',
    variantOf: 'container',
    componentType: ContainerComponent,
    isListed: false,
    name: 'Navigation Header',
    width: { mode: 'full' },
    height: { mode: 'value', fixed: false, value: CONTAINER_DEFAULT_HEIGHT },
    slots: {
      ids: [CONTAINER_INITIAL_SLOT_ID],
      [CONTAINER_INITIAL_SLOT_ID]: {
        children: [],
        alignment: {
          horizontal: 'left',
          vertical: 'middle',
          syncVerticalLineAlignment: true,
        } satisfies SlotAlignment,
      },
    },
    ioConfig: getIoConfig(),
    propsConfig: getNavigationContainerPropsConfig('navHeaderContainer'),
    slotPropsConfig: () => getNavigationContainerSlotPropsConfig('navHeaderContainer'),
    defaults: getDefaults(),
    slotDefaults: getContainerSlotDefaults(),
    styleIncludePaths: ['style'],
    features: {
      topLevelContainer: true,
      resizeWithChild: true,
    },
    version: 1,
  };
}

registerElementDefinition(getNavHeaderContainerDefinition());

export type NavHeaderContainerPropsModel = BaseContainerPropsModel & {
  content: {
    isSticky?: boolean;
  };
};

function getDefaults(): PropsOf<'navHeaderContainer'> {
  return {
    ...getContainerDefaults(),
    content: {
      isSticky: true,
    },
  };
}
